import {
  type BiomeId,
  buyPrice,
  carriedEntries,
  getItem,
  isBetterThanWorn,
  type ItemTemplate,
  merchantStock,
} from '@shefira/shared';

import { AUCTION_SLOT_LABELS } from './auction-format.js';
import { stepper } from './bench.js';
import { compareWithWorn } from './item-compare.js';
import { describeOnHover } from './item-hover.js';
import { addBetterArrow } from './item-icon.js';
import { formatGold, type MerchantView, merchantRow, section } from './merchant-row.js';
import { uiFormat, uiText } from './text.js';

/**
 * The merchant's stall (ADR 0016): the common pieces of his biome, then the
 * supplies every merchant keeps, each with its price set against the purse.
 * A supply is bought by the count the stepper says, never more than the
 * purse allows: the button prints what it will cost.
 */

export interface MerchantBuyOptions {
  biome: () => BiomeId | undefined;
  zoneId: () => string | undefined;
  onBuy: (templateId: string, quantity: number) => void;
}

/** One at a time stays the default: a stray click never spends a fortune. */
const MAX_COUNT = 20;

export class MerchantBuyView {
  /** The count chosen per supply, kept while the window lives. */
  private readonly counts = new Map<string, number>();

  constructor(private readonly options: MerchantBuyOptions) {}

  render(into: HTMLElement, view: MerchantView): void {
    const biome = this.options.biome();
    if (biome === undefined) return;
    const stock = merchantStock(biome, this.options.zoneId()).flatMap((id) => {
      const template = getItem(id);
      return template === undefined ? [] : [template];
    });
    const pieces = stock.filter((template) => template.kind === 'equipment');
    const supplies = stock.filter((template) => template.kind !== 'equipment');
    if (pieces.length > 0) {
      into.append(section(uiText('Équipement')));
      for (const template of pieces) into.append(this.row(template, view));
    }
    if (supplies.length > 0) {
      into.append(section(uiText('Provisions')));
      for (const template of supplies) into.append(this.row(template, view));
    }
  }

  private row(template: ItemTemplate, view: MerchantView): HTMLElement {
    const purse = view.inventory?.gold ?? 0;
    const each = buyPrice(template);
    const stackable = template.maxStack > 1;
    const most = Math.max(1, Math.min(MAX_COUNT, template.maxStack, Math.floor(purse / each)));
    const count = stackable ? Math.min(this.counts.get(template.id) ?? 1, most) : 1;
    const total = each * count;

    const { row, slot, icon, text, meta, actions } = merchantRow(template, view.icons);
    row.dataset.template = template.id;
    // A ware is sold new: at +0 and without affixes.
    if (isBetterThanWorn({ template, upgradeLevel: 0 }, view.wearer, view.inventory?.equipped)) {
      addBetterArrow(icon);
    }
    meta.append(this.metaOf(template, view));
    const price = document.createElement('span');
    price.className = purse >= total ? 'bench__price' : 'bench__price bench__price--short';
    price.textContent =
      count > 1
        ? uiFormat('{total} ({each} l’unité)', {
            total: formatGold(total),
            each: formatGold(each),
          })
        : formatGold(each);
    text.append(price);

    const context = () => {
      const compare = compareWithWorn(template, view.inventory?.equipped);
      return { wearer: view.wearer, details: compare === undefined ? {} : { compare } };
    };
    describeOnHover(slot, template, context);
    describeOnHover(text, template, context);

    if (stackable) {
      actions.append(
        stepper({
          count,
          most,
          onStep: (next) => {
            this.counts.set(template.id, next);
            view.redraw();
          },
        }),
      );
    }
    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'smith__go merchant__sell';
    buy.textContent = uiText('Acheter');
    buy.disabled = !view.inReach || purse < total;
    buy.addEventListener('click', () => {
      this.options.onBuy(template.id, count);
    });
    actions.append(buy);
    return row;
  }

  /** What to know before paying: the slot and the level for a piece, how many one has of a supply. */
  private metaOf(template: ItemTemplate, view: MerchantView): Node {
    const line = document.createDocumentFragment();
    if (template.kind === 'equipment') {
      const label = template.slot === undefined ? undefined : AUCTION_SLOT_LABELS[template.slot];
      if (label !== undefined) line.append(uiText(label));
      if (template.levelRequired > 1) {
        const level = document.createElement('span');
        if (template.levelRequired > view.wearer.level) level.className = 'bench__meta--short';
        level.textContent = uiFormat('niveau {level}', { level: template.levelRequired });
        if (label !== undefined) line.append(' · ');
        line.append(level);
      }
      return line;
    }
    const owned = view.inventory
      ? carriedEntries(view.inventory)
          .filter((entry) => entry.item.templateId === template.id)
          .reduce((sum, entry) => sum + entry.item.quantity, 0)
      : 0;
    line.append(
      owned > 0
        ? uiFormat('Dans ton sac : {count}', { count: owned })
        : uiText('Aucun dans ton sac'),
    );
    return line;
  }
}
